import { useEffect, useState } from 'react';
import type { SplitDetails } from '@/api/getSplitDetails';
import { useSplitDetails } from './useSplitDetails';

interface UseSplitStateResult {
  isLoading: boolean;
  isSplitExist: boolean;
  isSplitPending: boolean;
  isSplitExpired: boolean;
  splitDetails: SplitDetails | null;
}

interface UseSplitStateParams {
  orderNumber: string;
  enableSmartPolling?: boolean;
}

export const useSplitState = ({
  orderNumber,
  enableSmartPolling = true,
}: UseSplitStateParams): UseSplitStateResult => {
  const [pollingInterval, setPollingInterval] = useState(30000); // Start with 30 seconds

  const { data, isLoading } = useSplitDetails({
    orderNumber,
    enablePolling: enableSmartPolling,
    pollingInterval,
  });

  // Smart polling: more frequent when no split exists, less frequent when split exists
  useEffect(() => {
    if (enableSmartPolling) {
      const hasSplit = data && !('errorCode' in data);
      if (hasSplit) {
        // Split exists, poll less frequently (every 1 minutes)
        setPollingInterval(1 * 60 * 1000);
      } else {
        // No split yet, poll more frequently (every 15 seconds)
        setPollingInterval(15 * 1000);
      }
    }
  }, [data, enableSmartPolling]);

  if (isLoading) {
    return {
      isLoading: true,
      isSplitExist: false,
      isSplitPending: false,
      isSplitExpired: false,
      splitDetails: null,
    };
  }

  if (data && 'errorCode' in data) {
    return {
      isLoading: false,
      isSplitExist: false,
      isSplitPending: false,
      isSplitExpired: false,
      splitDetails: null,
    };
  }

  return {
    isLoading: false,
    isSplitExist: true,
    splitDetails: data || null,
    isSplitPending: !!(data && 'status' in data && data.status === 'PENDING'),
    isSplitExpired: !!(data && 'status' in data && data.status === 'EXPIRED'),
  };
};
